import { Injectable } from '@angular/core';
import { BaseApiService } from '@core/services/base-api.service';
import type { PaginatedResponseDto } from '@kerberos-compliance/compliance-shared-ts/common/dtos/response.paginated.dtos';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type { ResponseTrainingUserDto } from '@kerberos-compliance/compliance-shared-ts/features/training/dtos/response.training-user.dto';
import type { ListRequestQueryParameters } from '@kerberos-compliance/compliance-shared-ui';
import { buildPaginationSortParameters } from '@shared/utils/pagination-sort-parameters.utility';

type TrainingUsersQueryParameters = {
  page?: string;
  pageSize?: string;
  paginationToken?: string;
  sortField?: string;
  sortOrder?: string;
  countAll: string;
  organizationIds: string[];
};

const EndpointTrainingUsers = {
  users: 'training/users',
} as const;

@Injectable()
export class TrainingUserListService extends BaseApiService {
  protected readonly featureRole = NamedRoleMap.InternalUser;

  /**
   * Fetches the paginated list of academy users for a given organization.
   *
   * @param {UUID} organizationId - Organization id to scope the academy users to.
   * @param {ListRequestQueryParameters<never>} queryData - Pagination and sort parameters.
   * @returns {Promise<PaginatedResponseDto<ResponseTrainingUserDto>>} Paginated academy users for the organization.
   */
  public async getTrainingUsers(
    organizationId: UUID,
    queryData: ListRequestQueryParameters<never>,
  ): Promise<PaginatedResponseDto<ResponseTrainingUserDto>> {
    const parameters: TrainingUsersQueryParameters = {
      ...buildPaginationSortParameters(queryData),
      countAll: String(queryData.countAll ?? true),
      organizationIds: [organizationId],
    };

    return this.get<PaginatedResponseDto<ResponseTrainingUserDto>, TrainingUsersQueryParameters>(
      EndpointTrainingUsers.users,
      this.getRequestOptions<TrainingUsersQueryParameters>(this.featureRole, { params: parameters }),
    );
  }

  /**
   * Constructs the academy users export URL.
   *
   * @returns {string} The fully constructed export URL.
   */
  public buildTrainingUsersExportFileUrl(): string {
    return `${this.baseUrl}/${EndpointTrainingUsers.users}`;
  }
}
